/**
 * Event registry — single source of truth for event-name → Command[] expansion.
 * Shared between parent widget script and iframe React app.
 *
 * Naming: dot.notation (`property.open`, `property.open.with-lendi`).
 * Primitives are exposed 1:1 alongside composites for host flexibility.
 *
 * Security:
 * - Events MUST be side-effect-free (navigation + UI state only).
 *   No mutations, no form submits, no privileged operations.
 * - All event params validated; malformed input throws.
 * - Static registry — no eval, no dynamic handler injection.
 */
import type { Command, EventHandler, EventParams } from './types';

const ID_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;

const validateId = (raw: unknown, eventName: string): string => {
  if (typeof raw !== 'string' || !ID_PATTERN.test(raw)) {
    throw new Error(
      `[TwoPoint] event ${eventName}: 'id' invalid (expected ${ID_PATTERN.source})`
    );
  }
  return raw;
};

export const eventRegistry: Record<string, EventHandler> = {
  // Primitives — exposed for ad-hoc combinations
  'widget.scroll': (): Command[] => [{ kind: 'parent', action: 'widget.scrollIntoView' }],

  'lendi.expand': (): Command[] => [{ kind: 'iframe', action: 'lendi.expand', payload: {} }],

  'property.open': (params: EventParams): Command[] => {
    const propertyId = validateId(params.id, 'property.open');
    return [{ kind: 'iframe', action: 'property.open', payload: { propertyId } }];
  },

  // Composites — pre-baked scenarios
  'property.open.with-lendi': (params: EventParams): Command[] => {
    const propertyId = validateId(params.id, 'property.open.with-lendi');
    return [
      { kind: 'parent', action: 'widget.scrollIntoView' },
      { kind: 'iframe', action: 'property.open', payload: { propertyId } },
      { kind: 'iframe', action: 'lendi.expand', payload: {} },
    ];
  },
};

export const isKnownEvent = (name: string): boolean =>
  Object.prototype.hasOwnProperty.call(eventRegistry, name);

export const expandEvent = (name: string, params: EventParams): Command[] => {
  const handler = eventRegistry[name];
  if (!handler) throw new Error(`[TwoPoint] Unknown event: ${name}`);
  return handler(params);
};
